// Copyright 2025, Imprivata, Inc.  All rights reserved.

import { confirmAdminEmail, findImprIdpDirectories } from '@api/commonServices';
import { Endpoints, StorageKeys } from '@api/constants';
import type { ImprDir, InitialUser, PasswordPolicyGetResponse } from '@api/types';
import { createCodingCtxV2 } from '@api/utils';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { AppError } from '@errorHandler/errors';
import { Button, InputBox, Modal } from '@imprivata-cloud/components';
import { featureSupported } from '@/featureFlagsUtils';
import { Regex } from '@utils/utils';
import { Alert, Form } from 'antd';
import { CircleCheck } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetPasswordPolicy } from '@/self-service/hooks/useGetPasswordPolicy';
import CharacterCountHelper from '../../../components/characterCountHelper/CharacterCountHelper';
import ContinueButton from '../../../components/continueButton/ContinueButton';
import ConfirmOwnershipModal, {
  type ChallengeEffects,
} from '../../../components/otpChallenge/ConfirmOwnershipModal';
import ValidationRules, {
  type ValidationRule,
} from '../../../components/validationRules/ValidationRules';
import { STEPS } from '../../constants';
import { useAddImprIdpDirectory, useDirectoriesConfig } from '../../hooks/useImprivataIdp';
import { useNavigateToNextStep } from '../../hooks/useNavigateToNextStep';
import { getSetupContainer } from '../../layout/SetupPageLayout';

import './ImprivataIdp.less';
import { ErrorCode, ErrorMessage } from '@/errorHandler/constants';

const TRANSLATION_ROOT = `setup.${STEPS.IMPRIVATA_IDP}.content`;
const TRANSLATION_ERRORS = 'setup.external-idp-info.content.validation';

const DOMAIN_CONFLICT_CODES: readonly ErrorCode[] = [
  ErrorCode.DOMAIN_ALREADY_EXISTS,
  ErrorCode.DIFFERENT_CUSTOMER,
];

const SAVE_INLINE_ERROR_CODES: readonly ErrorCode[] = [
  ...DOMAIN_CONFLICT_CODES,
  ErrorCode.ALREADY_EXISTS,
];

const INPUT_CLASSNAME = 'impr-idp-input';
const SUBTITLE_CLASSNAME = 'impr-idp-subtitle';
const ADMIN_ACCOUNT_CLASSNAME = 'impr-idp-admin-account';
const VALIDATION_RULES_CONTAINER_CLASSNAME = 'validation-rules-container';

export interface ImprIdpFormValues {
  domainName: string;
  username: string;
  password: string;
  adminEmail: string;
}

interface PasswordPolicy {
  minimumLength: number;
  maximumLength: number;
}

type AdminEmailChallenge =
  | { kind: 'idle' }
  | {
      kind: 'challenging' | 'deferred';
      directoryId: string;
      userId: string;
      email: string;
      otpId: string;
      issuedAtMs: number;
    };

const IDLE: AdminEmailChallenge = { kind: 'idle' };

const DEFAULT_PASSWORD_POLICY: PasswordPolicy = { minimumLength: 15, maximumLength: 256 };

const resolvePasswordPolicy = (policyData?: PasswordPolicyGetResponse): PasswordPolicy =>
  policyData
    ? {
        minimumLength: policyData.minimumLength.value,
        maximumLength: policyData.maximumLength.value,
      }
    : DEFAULT_PASSWORD_POLICY;

const formatDomainSuffix = (suffix?: string) => (suffix ? `.${suffix}` : '');

const resolveDomainErrorMessage = (
  hasServerError: boolean,
  hasFormatError: boolean,
  t: (key: string) => string,
): string | undefined => {
  if (hasServerError) {
    return t('setup.errors.domain-already-exists');
  }
  if (hasFormatError) {
    return t(`${TRANSLATION_ERRORS}.enter-valid-domain`);
  }
  return undefined;
};

const validationMessage = (invalid: boolean, message: string): string | undefined =>
  invalid ? message : undefined;

const errorStatus = (message?: string) =>
  message ? { validateStatus: 'error' as const, help: message } : {};

const visibleClassName = (base: string, visible: boolean) =>
  `${base} ${visible ? 'is-visible' : ''}`;

const isChallengeActiveFor = (challenge: AdminEmailChallenge, email: string) =>
  challenge.kind !== 'idle' && challenge.email === email;

const isResumingDeferredChallenge = (
  challenge: AdminEmailChallenge,
  directoryId: string,
  email: string,
) =>
  challenge.kind === 'deferred' &&
  challenge.directoryId === directoryId &&
  challenge.email === email;

const hasDomainConflict = (code: ErrorCode | undefined): boolean =>
  code !== undefined && DOMAIN_CONFLICT_CODES.includes(code);

const persistImprIdpSession = (username: string, domainName: string) => {
  sessionStorage.setItem(StorageKeys.IMPR_IDP_USED, 'true');
  try {
    sessionStorage.setItem(StorageKeys.IMPR_IDP_USERNAME, username);
  } catch {
    /* storage full — username not stored */
  }
  try {
    sessionStorage.setItem(StorageKeys.IMPR_IDP_DOMAIN, domainName);
  } catch {
    /* storage full — domain not stored */
  }
};

const ImprivataIdp = () => {
  const navigateToNextStep = useNavigateToNextStep();
  const { mutateAsync: addIdpDirectory } = useAddImprIdpDirectory(
    Endpoints.SETUP_IMPR_IDP_DIR_ADD,
    SAVE_INLINE_ERROR_CODES,
  );
  const [form] = Form.useForm<ImprIdpFormValues>();
  const { t } = useTranslation();
  const emailVerificationEnabled = featureSupported('EMAIL_VERIFICATION_ENABLED');

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [domainName, setDomainName] = useState<string>();
  const [domainServerError, setDomainServerError] = useState(false);
  const verifiedBusinessEmail = localStorage.getItem(StorageKeys.ORG_EMAIL) ?? '';
  const [adminEmail, setAdminEmail] = useState(verifiedBusinessEmail);
  const [isEditingAdminEmail, setIsEditingAdminEmail] = useState(false);
  const [usernameServerError, setUsernameServerError] = useState(false);
  const [focusedField, setFocusedField] = useState<keyof ImprIdpFormValues | null>(null);
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  const [directory, setDirectory] = useState<ImprDir>();
  const lockedDomain = directory?.domains?.[0]?.domainName || undefined;
  const { emitError, emitSuccess, clearSuccessNotification } = useErrorContext();
  const { data: directoriesConfig } = useDirectoriesConfig(true);
  const imprDomainSuffix = formatDomainSuffix(directoriesConfig?.imprDomainSuffix);
  const domainSuffix = lockedDomain?.replace(/^[^.]*/, '') ?? imprDomainSuffix;
  const [isLoading, setIsLoading] = useState(false);
  const [challenge, setChallenge] = useState<AdminEmailChallenge>(IDLE);
  const [isAdminAccountCreated, setIsAdminAccountCreated] = useState(false);
  const { data: policyData } = useGetPasswordPolicy(true);
  const passwordPolicy = resolvePasswordPolicy(policyData);

  const handleSkip = useCallback(
    (e?: React.MouseEvent) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      navigateToNextStep(STEPS.EXTERNAL_IDP_INFO);
    },
    [navigateToNextStep],
  );

  function onSkipOrContinue() {
    navigateToNextStep(STEPS.EXTERNAL_IDP_INFO);
  }

  const domainRules: ValidationRule[] = [
    {
      id: 'length',
      label: value => `3 - 63 characters (${value.length}/63)`,
      validate: value => value.length >= 3 && value.length <= 63,
    },
    {
      id: 'characters',
      label: t(`${TRANSLATION_ROOT}.validation.domain`),
      validate: value => Regex.imprIdPDomain.test(value),
    },
    {
      id: 'hyphens',
      label: t(`${TRANSLATION_ROOT}.validation.domainHyphens`),
      validate: value => !value || (!value.startsWith('-') && !value.endsWith('-')),
    },
    {
      id: 'hyphenPosition',
      label: t(`${TRANSLATION_ROOT}.validation.domainHyphensPosition`),
      validate: value => value.length < 4 || Regex.imprIdPDomainHyphensPosition.test(value),
    },
  ];

  const adminEmailRules: ValidationRule[] = [
    {
      id: 'format',
      label: t(`${TRANSLATION_ERRORS}.enter-valid-email`),
      validate: value => Regex.email.test(value),
    },
  ];

  const usernameRules: ValidationRule[] = [
    {
      id: 'length',
      label: value => `6 - 64 characters (${value.length}/64)`,
      validate: value => value.length >= 6 && value.length <= 64,
    },
  ];

  const passwordRules: ValidationRule[] = [
    {
      id: 'min',
      label: `At least ${passwordPolicy.minimumLength} characters`,
      validate: value => value.length >= passwordPolicy.minimumLength,
    },
    {
      id: 'max',
      label: `Maximum ${passwordPolicy.maximumLength} characters`,
      validate: value => value.length <= passwordPolicy.maximumLength,
    },
  ];

  const isValid = (value: string, rules: ValidationRule[]) =>
    rules.every(rule => rule.validate(value));

  const hasError = (value: string, rules: ValidationRule[]) => !isValid(value, rules);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await findImprIdpDirectories(Endpoints.IMPR_IDP_DIR_FIND, true);
      if (response?.directories && response.directories.length > 0) {
        const directories = response.directories;
        const rawDomain = directories[0].domains?.[0]?.domainName;
        if (rawDomain) {
          const parsedDomain = rawDomain.split('.')[0];
          form.setFieldValue('domainName', parsedDomain);
          setDomainName(parsedDomain);
          setFocusedField(current => (current === 'domainName' ? null : current));
          setTouchedFields(current => {
            const next = new Set(current);
            next.delete('domainName');
            return next;
          });
          // Re-derive the sign-in domain on re-entry: sessionStorage does not survive
          // a browser session, so the completion page needs this rewritten here to show
          // directory guidance when the admin skips an already-configured step (ICP-2164).
          try {
            sessionStorage.setItem(StorageKeys.IMPR_IDP_DOMAIN, rawDomain);
          } catch {
            /* storage full — directory guidance omitted from the completion page */
          }
        }
        setDirectory(directories[0]);
        sessionStorage.setItem(StorageKeys.IMPR_IDP_USED, 'true');
      }
    } catch (error) {
      emitError(error as AppError);
    } finally {
      setIsLoading(false);
    }
  }, [form, emitError]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const validateForm = () => {
    const values = form.getFieldsValue();

    const validations = [
      !lockedDomain && hasError(values.domainName ?? '', domainRules),
      hasError(values.username ?? '', usernameRules),
      hasError(values.password ?? '', passwordRules),
      hasError(adminEmail.trim(), adminEmailRules),
      domainServerError,
    ];

    return validations.some(Boolean);
  };

  const shouldShowError = (
    fieldName: keyof ImprIdpFormValues,
    value: string,
    rules: ValidationRule[],
  ) => focusedField !== fieldName && touchedFields.has(fieldName) && hasError(value, rules);

  const shouldShowValidation = (fieldName: keyof ImprIdpFormValues) => {
    return focusedField === fieldName;
  };

  const domainErrorMessage = resolveDomainErrorMessage(
    domainServerError,
    shouldShowError('domainName', domainName ?? '', domainRules),
    t,
  );

  const currentEmail = adminEmail.trim();
  const isAdminEmailVerified =
    isValid(currentEmail, adminEmailRules) &&
    currentEmail.toLowerCase() === verifiedBusinessEmail.toLowerCase();
  const isSubmitBlocked = isChallengeActiveFor(challenge, currentEmail);

  useEffect(() => {
    if (!emailVerificationEnabled || !isAdminEmailVerified) {
      return undefined;
    }
    emitSuccess(t(`${TRANSLATION_ROOT}.admin-email-verified`));
    return () => clearSuccessNotification();
  }, [emailVerificationEnabled, isAdminEmailVerified, emitSuccess, clearSuccessNotification, t]);

  const handleFieldBlur = (fieldName: keyof ImprIdpFormValues) => {
    setFocusedField(null);
    setTouchedFields(prev => new Set(prev).add(fieldName));
  };

  const handleAdminEmailVerified = () => {
    setChallenge(IDLE);
    setIsAdminAccountCreated(true);
  };

  const requestAdminEmailChallenge = async (directoryId: string, email: string) => {
    const normalizedDomainName =
      lockedDomain ||
      `${(domainName ?? '').trim().toLowerCase().replaceAll('.', '-')}${imprDomainSuffix}`;
    const codingCtx = await createCodingCtxV2();
    const initialUser: InitialUser = {
      username: `${username.trim()}@${normalizedDomainName}`,
      password: codingCtx.encryptString(password),
      email,
    };
    const result = await addIdpDirectory({
      addDirectoryRequest: {
        directory: {
          domains: [{ domainName: normalizedDomainName, isPrimary: true }],
          directoryId,
        },
        initialUser,
      },
      codingContext: codingCtx,
    });
    return { otpId: result.otpId || '' };
  };

  const buildChallengeEffects = (c: AdminEmailChallenge): ChallengeEffects | null => {
    if (c.kind === 'idle') {
      return null;
    }
    return {
      request: () => requestAdminEmailChallenge(c.directoryId, c.email),
      confirm: (otpId: string, code: string) =>
        confirmAdminEmail(Endpoints.SETUP_ADMIN_EMAIL_CONFIRM, c.userId, otpId, code, c.email),
    };
  };

  const effects = buildChallengeEffects(challenge);

  const handleAlreadyExistsConflict = (
    error: AppError,
    directoryId: string,
    adminEmailToSubmit: string,
  ) => {
    if (isResumingDeferredChallenge(challenge, directoryId, adminEmailToSubmit)) {
      setChallenge(c => (c.kind === 'deferred' ? { ...c, kind: 'challenging' } : c));
      return;
    }
    if (error.message?.toLowerCase().includes('username')) {
      setUsername('');
      form.setFieldValue('username', '');
      setUsernameServerError(true);
    }
  };

  const submitAdminSetup = async (values: ImprIdpFormValues) => {
    const adminEmailToSubmit = adminEmail.trim();
    let directoryId =
      directory?.directoryId ??
      (challenge.kind !== 'idle' && challenge.email === adminEmailToSubmit
        ? challenge.directoryId
        : '');

    try {
      setIsLoading(true);
      setDomainServerError(false);
      const codingCtx = await createCodingCtxV2();
      const domainName =
        lockedDomain ||
        `${values?.domainName?.trim().toLowerCase().replaceAll('.', '-')}${imprDomainSuffix}`;
      const username = `${values?.username?.trim()}@${domainName}`;
      const password = codingCtx.encryptString(values?.password);

      const initialUser: InitialUser = { username, password, email: adminEmailToSubmit };

      const directoryPayload = {
        addDirectoryRequest: {
          directory: {
            domains: [{ domainName: domainName, isPrimary: true }],
            directoryId: directory?.directoryId,
          },
          initialUser,
        },
        codingContext: codingCtx,
      };

      const dirResponse = await addIdpDirectory(directoryPayload);
      directoryId = dirResponse.directoryId || directory?.directoryId || '';
      const userId = dirResponse.userId ?? '';

      if (!emailVerificationEnabled) {
        persistImprIdpSession(username, domainName);
        onSkipOrContinue();
      } else if (dirResponse.verified) {
        persistImprIdpSession(username, domainName);
        setIsAdminAccountCreated(true);
      } else if (dirResponse.otpId) {
        setChallenge({
          kind: 'challenging',
          directoryId,
          userId,
          email: adminEmailToSubmit,
          otpId: dirResponse.otpId,
          issuedAtMs: Date.now(),
        });
      } else {
        emitError(AppError.fromMessage(ErrorMessage.SYSTEM_ERROR, ErrorCode.UNKNOWN));
      }
    } catch (e) {
      const error = e as AppError;
      if (error.code === ErrorCode.ALREADY_EXISTS) {
        handleAlreadyExistsConflict(error, directoryId, adminEmailToSubmit);
        return;
      }
      if (hasDomainConflict(error.code)) {
        setDomainServerError(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResumeChallenge = () => submitAdminSetup(form.getFieldsValue());

  const onSubmit = (values: ImprIdpFormValues) => submitAdminSetup(values);

  const adminEmailErrorMessage = validationMessage(
    shouldShowError('adminEmail', adminEmail.trim(), adminEmailRules),
    t(`${TRANSLATION_ERRORS}.enter-valid-email`),
  );
  const usernameErrorMessage = usernameServerError
    ? t('setup.errors.already-exists')
    : validationMessage(
        shouldShowError('username', username, usernameRules),
        t(`${TRANSLATION_ROOT}.username-helper`, { count: username.length }),
      );
  const passwordErrorMessage = validationMessage(
    shouldShowError('password', password, passwordRules),
    t(`${TRANSLATION_ERRORS}.enter-valid-password`),
  );

  return (
    <div className="impr-idp">
      <div className={SUBTITLE_CLASSNAME}>{t(`${TRANSLATION_ROOT}.subtitle`)}</div>
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        aria-label={'impr-idp-form'}
        className="impr-idp-form"
        onFinish={onSubmit}
      >
        <div className="impr-idp-fields">
          <div className={INPUT_CLASSNAME}>
            <Form.Item name="domainName" {...errorStatus(domainErrorMessage)}>
              <InputBox
                size="large"
                label="Domain name"
                value={domainName}
                aria-label="domain"
                minLength={3}
                maxLength={63}
                disabled={!!lockedDomain}
                onFocus={() => setFocusedField('domainName')}
                onBlur={() => handleFieldBlur('domainName')}
                onChange={e => {
                  const value = e.target.value.replaceAll('.', '-');
                  setDomainName(value);
                  setDomainServerError(false);
                  form.setFieldValue('domainName', value);
                }}
              />
            </Form.Item>
            <span className="impr-idp-input-suffix">{domainSuffix}</span>
          </div>

          <div
            className={visibleClassName(
              VALIDATION_RULES_CONTAINER_CLASSNAME,
              shouldShowValidation('domainName'),
            )}
          >
            <CharacterCountHelper
              className="impr-idp-input-helper"
              text={t(`${TRANSLATION_ROOT}.domain-helper`, { count: (domainName ?? '').length })}
            />
          </div>

          <div style={{ height: '12px' }} />

          {isEditingAdminEmail ? (
            <>
              <div className={ADMIN_ACCOUNT_CLASSNAME}>
                {t(`${TRANSLATION_ROOT}.acct-label-prefix`)}
              </div>
              <div className={INPUT_CLASSNAME}>
                <Form.Item
                  name="adminEmail"
                  initialValue={adminEmail}
                  validateStatus={adminEmailErrorMessage ? 'error' : undefined}
                  help={t(`${TRANSLATION_ERRORS}.enter-valid-email`)}
                >
                  <InputBox
                    autoFocus
                    size="large"
                    aria-label="admin email"
                    value={adminEmail}
                    onFocus={() => setFocusedField('adminEmail')}
                    onBlur={() => handleFieldBlur('adminEmail')}
                    onChange={e => {
                      const value = e.target.value;
                      setAdminEmail(value);
                      form.setFieldValue('adminEmail', value);
                    }}
                  />
                </Form.Item>
                <button
                  type="button"
                  className="change-email-button impr-idp-input-action"
                  onClick={() => setIsEditingAdminEmail(false)}
                  disabled={hasError(currentEmail, adminEmailRules)}
                >
                  {t('common.save')}
                </button>
              </div>
            </>
          ) : (
            <div className={ADMIN_ACCOUNT_CLASSNAME}>
              {t(`${TRANSLATION_ROOT}.acct-label-prefix`)} <strong>{currentEmail}</strong>{' '}
              <button
                type="button"
                className="change-email-button"
                onClick={() => setIsEditingAdminEmail(true)}
              >
                {t(`${TRANSLATION_ROOT}.acct-label-change`)}
              </button>
            </div>
          )}

          <div style={{ height: '12px' }} />

          <div className={`${INPUT_CLASSNAME} ${INPUT_CLASSNAME}--full`}>
            <Form.Item name="username" {...errorStatus(usernameErrorMessage)}>
              <InputBox
                autoFocus
                size="large"
                aria-label={'username'}
                label={t('portal.appearance.username-label')}
                minLength={6}
                maxLength={64}
                value={username}
                onFocus={() => setFocusedField('username')}
                onBlur={() => handleFieldBlur('username')}
                onChange={e => {
                  const value = e.target.value;
                  setUsername(value);
                  // Only clear server error if user is typing a new value (not empty)
                  if (value) {
                    setUsernameServerError(false);
                  }
                  form.setFieldValue('username', value);
                }}
              />
            </Form.Item>
          </div>
          <div
            className={visibleClassName(
              VALIDATION_RULES_CONTAINER_CLASSNAME,
              shouldShowValidation('username'),
            )}
          >
            <CharacterCountHelper
              text={t(`${TRANSLATION_ROOT}.username-helper`, { count: username.length })}
            />
          </div>

          <div style={{ height: '12px' }} />

          <div className={`${INPUT_CLASSNAME} ${INPUT_CLASSNAME}--full`}>
            <Form.Item
              name="password"
              rules={[{ required: true }]}
              {...errorStatus(passwordErrorMessage)}
            >
              <InputBox
                size="large"
                aria-label={'password'}
                label={t('portal.appearance.password-label')}
                minLength={passwordPolicy.minimumLength}
                maxLength={passwordPolicy.maximumLength}
                type="password"
                value={password}
                onFocus={() => setFocusedField('password')}
                onBlur={() => handleFieldBlur('password')}
                onChange={e => {
                  setPassword(e.target.value);

                  form.setFieldValue('password', e.target.value);
                }}
              />
            </Form.Item>
          </div>
          <div
            className={visibleClassName(
              'validation-rules-container password-validation-rules',
              shouldShowValidation('password'),
            )}
          >
            <ValidationRules value={password} rules={passwordRules} />
          </div>
        </div>

        <div className="impr-idp-actions">
          <Form.Item className={'impr-idp-continue-button'}>
            <ContinueButton
              disabled={isLoading || isSubmitBlocked || isEditingAdminEmail}
              label={'Continue'}
              htmlType="submit"
              overrideErrorCheck={() => validateForm()}
              validateDirty={false}
            />
          </Form.Item>
          <Button
            label={t('common.skip')}
            onClick={onSkipOrContinue}
            onMouseDown={handleSkip}
            size="large"
            className={'impr-idp-skip-button'}
          />
        </div>
      </Form>
      {isAdminAccountCreated && (
        <Modal
          open
          getContainer={getSetupContainer}
          closable={false}
          maskClosable={false}
          width={432}
          className="admin-account-created-modal"
          wrapClassName="admin-account-created-modal-wrap"
          footer={(_, { OkBtn }) => <OkBtn />}
          onOk={onSkipOrContinue}
          okText={t('common.continue-button')}
        >
          <CircleCheck className="modal-graphic success" size={48} aria-hidden="true" />
          <h2>{t(`${TRANSLATION_ROOT}.admin-account-created-title`)}</h2>
          <p>{t(`${TRANSLATION_ROOT}.admin-account-created-description`)}</p>
        </Modal>
      )}
      {emailVerificationEnabled && challenge.kind === 'challenging' && effects && (
        <ConfirmOwnershipModal
          key={`admin-email-otp-${challenge.email}`}
          getContainer={getSetupContainer}
          open
          email={challenge.email}
          otpId={challenge.otpId}
          codeIssuedAtMs={challenge.issuedAtMs}
          effects={effects}
          translationRoot={`${TRANSLATION_ROOT}.admin-email-otp`}
          onResent={(otpId, issuedAtMs) =>
            setChallenge(c => (c.kind === 'challenging' ? { ...c, otpId, issuedAtMs } : c))
          }
          onVerified={handleAdminEmailVerified}
          onCancel={() =>
            setChallenge(c => (c.kind === 'challenging' ? { ...c, kind: 'deferred' } : c))
          }
        />
      )}
      {emailVerificationEnabled && challenge.kind === 'deferred' && (
        <Alert
          type="info"
          message={t(`${TRANSLATION_ROOT}.admin-email-verification-pending`)}
          description={t(`${TRANSLATION_ROOT}.admin-email-verification-pending-description`)}
          action={
            <Button
              label={t(`${TRANSLATION_ROOT}.verify-email`)}
              size="small"
              onClick={handleResumeChallenge}
            />
          }
        />
      )}
    </div>
  );
};

export default ImprivataIdp;
